/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Empty, Modal, Typography } from 'antd';
import { FullscreenOutlined, PieChartOutlined } from '@ant-design/icons';
import AnalyticsTable from '../Tables/AnalyticsTable';
import EchartsWrapper from './EchartsWrapper';
import ChartLoader from './ChartLoader';

// Misc imports
import is from '../../utils/is';
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { formatNumberToThousandSeparator } from '../../utils/string/formatNumberToThousandSeparator';

const DecisionTreesUsageSection = ({ data = [], sectionTitle = '', seriesName = '', isLoading = true, renderOnlyChart = false, renderOnlyTable = false }) => {
  const [chartToFullscreen, setChartToFullscreen] = useState();
  const { t } = useTranslation();

  const [sortedInfo, setSortedInfo] = useState({ order: '', columnKey: '' });

  const handleTableChange = (_pagination, _filters, sorter) => {
    setSortedInfo(sorter);
  };

  const getOptions = (pieData) => {
    const xData = pieData.map(item => item.name);
    const data = pieData.map((item) => ({ name: item.name, value: item.count }));

    return {
      legend: {
        type: 'scroll',
        show: true,
        data: xData,
        bottom: 0
      },
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          const { name, value, marker } = params;
          return `${seriesName}<br />${marker}${name} : ${formatNumberToThousandSeparator(value)} ${t('interactions')}`;
        },
        confine: true,
        triggerOn: 'mousemove|click',
        enterable: true,
        renderMode: 'html'
      },
      series: [
        {
          name: seriesName,
          bottom: '5%',
          type: 'pie',
          startAngle: 40,
          data: data,
          radius: '70%',
          center: ['50%', '50%'],
          label: {
            formatter: (params) => {
              return `${params.value}`;
            }
          },
          emphasis: {
            label: {
              show: true
            }
          }
        }
      ]
    };
  };

  const tableConfig = {
    scroll: 180,
    tableData: data,
    isDescriptionTable: false,
    noScroll: renderOnlyTable,
    isTableLoading: isLoading,
    emptyLines: 0,
    tableColumns: [
      {
        title: t('Decision Tree'),
        dataIndex: 'name',
        key: 'name',
        width: '70%',
        align: 'left',
        ellipsis: true,
        sorter: (a, b) => a.name.localeCompare(b.name),
        sortOrder: sortedInfo.columnKey === 'name' && sortedInfo.order
      },
      {
        title: t('Interactions'),
        dataIndex: 'count',
        key: 'count',
        width: '30%',
        align: 'center',
        ellipsis: true,
        sorter: (a, b) => a.count - b.count,
        sortOrder: sortedInfo.columnKey === 'count' && sortedInfo.order
      }
    ]
  };

  const chart = (
    <Fragment>
      {
        !isEmptyArray(data) ? (
          <EchartsWrapper
            chartOptions={getOptions(data)}
            style={{ height: 367 }}
            loading={isLoading}
          />
        ) : (
          <div className="d-flex content-fill flex-column h-100">
            <div className="mt-5 d-flex align-items-center mb-auto text-center flex-auto align-self-center">
              <Empty
                description={(
                  <Typography.Paragraph>
                    {t('No data available for this chart')}
                  </Typography.Paragraph>
                )}
              />
            </div>
          </div>
        )
      }
    </Fragment>
  );

  return renderOnlyChart ? chart : (
    <Fragment>
      {
        renderOnlyTable ? (
          <AnalyticsTable
            tableData={tableConfig.tableData}
            isDescriptionTable={tableConfig.isDescriptionTable}
            isTableLoading={tableConfig.isTableLoading}
            emptyLines={tableConfig.emptyLines}
            tableColumns={tableConfig.tableColumns}
            scroll={tableConfig.scroll}
            noScroll={tableConfig.noScroll}
            onChange={handleTableChange}
          />
        ) : (
          <Fragment>
            <div className="mb-6 position-relative">
              <Typography.Title level={4} className="mb-6">
                <Typography.Text className="mr-2">{sectionTitle}</Typography.Text>
                <PieChartOutlined className="ml-2" />
              </Typography.Title>

              {isLoading ? <ChartLoader /> : chart}

              <Button
                type="text"
                size="large"
                disabled={isLoading}
                icon={<FullscreenOutlined />}
                className="position-absolute top-0 right-0"
                onClick={() => setChartToFullscreen(chart)}
              />
            </div>

            <AnalyticsTable
              tableData={tableConfig.tableData}
              isDescriptionTable={tableConfig.isDescriptionTable}
              isTableLoading={tableConfig.isTableLoading}
              emptyLines={tableConfig.emptyLines}
              tableColumns={tableConfig.tableColumns}
              scroll={tableConfig.scroll}
              onChange={handleTableChange}
            />

            <Modal
              open={is.truthy(chartToFullscreen)}
              onCancel={() => setChartToFullscreen()}
              destroyOnClose
              footer={null}
              centered
              style={{ maxWidth: 1000 }}
              width={'95%'}
            >
              <div className="p-5">
                {chartToFullscreen}
              </div>
            </Modal>
          </Fragment>
        )
      }
    </Fragment>
  );
};

DecisionTreesUsageSection.propTypes = {
  data: PropTypes.array,
  sectionTitle: PropTypes.string,
  seriesName: PropTypes.string,
  isLoading: PropTypes.bool,
  renderOnlyChart: PropTypes.bool,
  renderOnlyTable: PropTypes.bool
};

export default DecisionTreesUsageSection;
